/**
 * The payment step's behavior half (design D8): the app's payment flow (usePaymentFlow, the same
 * controller as the app's payment page, shared-client-screens D4) on the website session: the card
 * panel's intent request and the payment status read through `webFetch` (so a saved card asks for
 * its code), which methods show from the flow's own availability (card only: the website offers no
 * Venmo, row P1), and once confirmed the booked page. Draws nothing.
 */
import { useCallback, useMemo, type RefObject } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { CardCheckoutPanelHandle } from '@/components/payment/CardCheckoutPanel';
import { formatCurrency } from '@/components/payment/formatCurrency';
import type { CreatePaymentIntentResponse, PaymentStatusResponse } from '@/api/types';
import { mapCheckoutError, type CheckoutError } from '@/hooks/checkoutError';
import { usePaymentFlow, type PaymentFlowPhase, type PaymentFlowTransport, type PaymentStatusRead } from '@/hooks/payment/usePaymentFlow';
import type { PaymentIntentRequest } from '@/hooks/useCreatePaymentIntent';
import { declineNotice, paymentNotice, paymentView } from '@/pages/booking/shared/bookingRules';
import { bookingPaymentSubject, checkoutEntryIdentity, type CheckoutEntry } from '@/types/checkout';

import { CLIENT_SCOPE, webFetch } from '../../api/session';
import { useSiteStudio } from '../../api/site';
import { flowKeys, useWebFlowContext } from '../../hooks/useWebFlow';
import { usePieceEmit, usePieceOptions, type PieceInput, type PieceRuntime } from '../../contract/runtime';
import { usePageMover } from '../../pageMover';
import { contract, type PaymentStepActions, type PaymentStepEvents, type PaymentStepState } from './contract';

export interface PaymentStepInput extends PieceInput<PaymentStepEvents> {
  /** The held flow at checkout, or an approved request paid afterwards (Pay now). */
  readonly entry: CheckoutEntry;
  /** What is paid now, in dollars. */
  readonly amount: number;
  /** Pick a new time, when too close to the start to pay: one step back. */
  readonly onPickNewTime?: () => void;
  /** The checkout asks for the number to call (service-by-phone D5): paying waits for it. */
  readonly waitingForNumber?: boolean;
}

export type PaymentStepRuntime = PieceRuntime<typeof contract, PaymentStepState, PaymentStepActions> & {
  /** The payment flow's own phase, for the app's payment screen (PaymentStepScreen). */
  readonly flowPhase: PaymentFlowPhase;
  /** Back from a Stripe hand-off, its payment is being read: the phone's card form stays away meanwhile. */
  readonly resuming: boolean;
  /** The app's card panel: its handle, its intent request and its reports. */
  readonly panel: {
    readonly key: number;
    readonly ref: RefObject<CardCheckoutPanelHandle>;
    readonly entry: CheckoutEntry;
    readonly intentRequest: PaymentIntentRequest;
    readonly onCompleteChange: (complete: boolean) => void;
    readonly onIntentReady: (expiresAt: string | null) => void;
    readonly onSubmitInFlightChange: (inFlight: boolean) => void;
    readonly onPaymentSubmitted: (p: { stripePaymentIntentId: string }) => void;
    readonly onDecline: (d: { title: string } | null) => void;
    readonly onError: (error: CheckoutError) => void;
    readonly onConflict: () => void;
  };
};

/** The contract's names for the payment flow's phases (they only grow). */
const PHASES: Record<PaymentFlowPhase, PaymentStepState['phase']> = {
  form: 'form',
  confirming: 'confirming',
  slowConfirm: 'slowConfirm',
  windowTooClose: 'lapsed',
  benignTerminal: 'alreadyPaid',
};

const intentRequest: PaymentIntentRequest = (body, idempotencyKey) =>
  webFetch<CreatePaymentIntentResponse>('/payment/intent', {
    method: 'POST',
    headers: { 'Idempotency-Key': idempotencyKey },
    body: JSON.stringify(body),
  });

/** The payment status on the website session, read every two seconds until it settles. */
function useWebPaymentStatus(entry: CheckoutEntry, intentId: string | null, { enabled }: { enabled: boolean }): PaymentStatusRead {
  const status = useQuery<PaymentStatusResponse, CheckoutError>({
    queryKey: [...CLIENT_SCOPE, 'payment-status', checkoutEntryIdentity(entry), intentId],
    queryFn: async () => {
      const who: Record<string, string> = bookingPaymentSubject(entry);
      try {
        return await webFetch<PaymentStatusResponse>(`/payment/status?${new URLSearchParams({ ...who, stripePaymentIntentId: intentId! })}`);
      } catch (error) {
        throw mapCheckoutError(error);
      }
    },
    enabled: enabled && Boolean(intentId),
    retry: false,
    refetchInterval: (q) => (q.state.data?.state === 'CONFIRMED' || q.state.data?.state === 'LAPSED' ? false : 2000),
  });
  return { data: status.data, error: status.error };
}

export function usePaymentStep(input: PaymentStepInput): PaymentStepRuntime {
  const options = usePieceOptions(contract, input.options);
  const emit = usePieceEmit(input.onEvent);
  const navigate = usePageMover();
  const queryClient = useQueryClient();
  const { entry, onPickNewTime } = input;
  // A held flow carries its availability and its hold; Pay now (an approved request) is card only, the server gating it
  // (a refusal shows the hard block).
  const flowToken = entry.kind === 'flow' ? entry.flowToken : null;
  const flowContext = useWebFlowContext(flowToken).data;
  const studioName = useSiteStudio().data?.name;

  // A held flow is read again; Pay now has no flow, so its booking is (as the app's post-approval payment).
  const bookingId = entry.kind === 'postApproval' ? entry.bookingId : null;
  const refetchFlow = useCallback(() => {
    if (flowToken) void queryClient.invalidateQueries({ queryKey: flowKeys.context(flowToken) });
    else if (bookingId) void queryClient.invalidateQueries({ queryKey: [...CLIENT_SCOPE, 'booking', bookingId] });
  }, [flowToken, bookingId, queryClient]);
  const transport = useMemo<PaymentFlowTransport>(() => ({ intentRequest, useStatus: useWebPaymentStatus, refetchFlow }), [refetchFlow]);
  const flow = usePaymentFlow({
    entry,
    transport,
    reservation: { eventHoldId: flowContext?.flow.booking?.id, placeholderId: flowContext?.flow.placeholderId },
    holdExpiresAt: flowContext?.flow.placeholderExpiresAt,
    onBooked: (bookingId) => {
      if (!bookingId) return;
      emit('paid', { bookingId });
      // The booking read held from before the payment still says payment due, and the booked page would send it back
      // to Pay now for a moment: read it fresh first, so the move lands once (card 4 walk, Gabriel 2026-10-31).
      void queryClient
        .refetchQueries({ queryKey: [...CLIENT_SCOPE, 'booking', bookingId] })
        .finally(() => navigate(`/booked/${encodeURIComponent(bookingId)}`, { replace: true }));
    },
  });

  const methods = flowToken ? paymentView(flowContext?.context.service, { venmo: false }) : flow.cardRefused ? 'hardBlock' : 'cardOnly';
  const view: PaymentStepState['view'] = methods === 'depositBlocked' || methods === 'hardBlock' ? methods : methods === 'cardOnly' || methods === 'bothMethods' ? 'cardOnly' : 'hardBlock';
  const businessName = flowContext?.business.name ?? studioName ?? 'the business';
  const notice =
    flow.phase === 'windowTooClose' || flow.phase === 'benignTerminal'
      ? paymentNotice(flow.phase, businessName)
      : view !== 'cardOnly'
        ? paymentNotice(view, businessName, { booked: entry.kind === 'postApproval' })
        : null;

  const { pay } = flow;
  const waitingForNumber = input.waitingForNumber ?? false;
  const actions = useMemo<PaymentStepActions>(
    () => ({ pay: () => (waitingForNumber ? undefined : pay()), pickNewTime: () => onPickNewTime?.(), done: () => navigate('/') }),
    [pay, waitingForNumber, onPickNewTime, navigate],
  );

  return {
    state: {
      amount: formatCurrency(input.amount),
      complete: flow.complete && !flow.timerExpired,
      phase: PHASES[flow.phase],
      paying: flow.submitting,
      waitingForNumber,
      decline: flow.decline?.title ?? null,
      // The website offers no other method beside the card (row P6).
      declineNote: flow.decline ? declineNotice(flow.decline.title, false).body : null,
      view,
      notice,
      businessName,
    },
    actions,
    options,
    flowPhase: flow.phase,
    resuming: flow.resuming,
    panel: flow.panel,
  };
}
